/**
 * Streets (from arrival prototype D): each line of a moment holding two or more of its district's own buildings is a
 * street between their doors, chalked while the learner does not yet know every word of it and paved once they do
 * (v1 ticket 31's rule, worked out by the server: `GET /api/town` sends each district's `streets`, building keys in
 * line order and `paved`, never the line). Both looks draw them from the geometry here: D in the diorama
 * (d-streets.ts), B on its ground (night-town.ts). DOM-free, so the node tests run it.
 *
 * The OwnTown helpers let the landing and the round hold streets back and bring them on screen one at a time: a
 * district's streets left out (`withoutStreets`), chalked up to n (`chalkedUpTo`), one paved (`pavedOne`), the streets
 * of an earlier town kept (`streetsFrom`), and the streets an answer paved (`newlyPaved`).
 */
import type { Layout, Lot } from './layout';
import type { OwnTown } from './own-town';
import type { Town } from './town-data';

/** A line's street as the server sends it: the district's own buildings it holds, in line order. */
export interface Street { line: number; keys: string[]; paved: boolean }

/** One straight leg of a street on the ground, world units: [x0, y0, x1, y1]. */
export type Leg = [number, number, number, number];

/** A street laid out in a town: where it runs, and whether it is paved. */
export interface LaidStreet { district: string; line: number; paved: boolean; legs: Leg[] }

type F = (a: number, b: number) => [number, number];
/** A lot's frame: a along its row, b from the back (0) to the lane in front (1). */
export const frameOf = (l: Lot): F => (l.b.axis === 0 ? (a, b) => [l.gx + a, l.gy + b] : (a, b) => [l.gx + b, l.gy + 1 - a]);

/** A lot's door on the lane, on the ground. */
export const doorOf = (l: Lot): [number, number] => frameOf(l)(0.5, 0.93);

/**
 * Axis-aligned legs from door to door along the lane, like D's walkway: along the first door's lane, then across.
 * The doors in reading order; none when fewer than two of the keys have a lot.
 */
export function streetLegs(L: Layout, keys: readonly string[]): Leg[] {
  const lots = keys.map((k) => L.byKey.get(k)).filter((l): l is Lot => !!l);
  if (lots.length < 2) return [];
  const doors = lots.map(doorOf).sort((p, q) => p[0] - q[0] || p[1] - q[1]);
  const legs: Leg[] = [];
  for (let i = 1; i < doors.length; i++) {
    const [px, py] = doors[i - 1], [qx, qy] = doors[i];
    if (px !== qx) legs.push([px, py, qx, py]);
    if (py !== qy) legs.push([qx, py, qx, qy]);
  }
  return legs;
}

/** Every street of the town that has somewhere to run, in district order then line order. */
export function laidStreets(town: Town, L: Layout): LaidStreet[] {
  return town.districts.flatMap((d) => d.streets.flatMap((s) => {
    const legs = streetLegs(L, s.keys);
    return legs.length ? [{ district: d.id, line: s.line, paved: s.paved, legs }] : [];
  }));
}

/** A street's two ends, for an effect that runs along it. */
export function streetEnds(legs: readonly Leg[]): { from: [number, number]; to: [number, number] } | null {
  if (!legs.length) return null;
  const a = legs[0], b = legs[legs.length - 1];
  return { from: [a[0], a[1]], to: [b[2], b[3]] };
}

// ---- the own town's streets, held back and brought on ----------------------------------------------------------

type D = OwnTown['districts'][number];
const mapDistrict = (t: OwnTown, id: string, f: (d: D) => D): OwnTown => ({ ...t, districts: t.districts.map((d) => (d.id === id ? f(d) : d)) });

/** A district's streets, as `t` has them. */
export const streetsOf = (t: OwnTown, id: string): Street[] => t.districts.find((d) => d.id === id)?.streets ?? [];

/** `t` with a district's streets left out (drawn later, one at a time). */
export const withoutStreets = (t: OwnTown, id: string): OwnTown => mapDistrict(t, id, (d) => ({ ...d, streets: [] }));

/** `t` with the first `n` of a district's streets from `full` on the ground, chalked (never paved yet). */
export const chalkedUpTo = (t: OwnTown, full: OwnTown, id: string, n: number, paved = false): OwnTown =>
  mapDistrict(t, id, (d) => ({ ...d, streets: streetsOf(full, id).slice(0, n).map((s) => ({ ...s, paved: paved && s.paved })) }));

/** `t` with each district's streets as `src` has them (a district `src` lacks keeps its own). */
export function streetsFrom(t: OwnTown, src: OwnTown): OwnTown {
  const by = new Map(src.districts.map((d) => [d.id, d.streets ?? []]));
  return { ...t, districts: t.districts.map((d) => (by.has(d.id) ? { ...d, streets: by.get(d.id) } : d)) };
}

/** `t` with one street paved (added when it has none for that line). */
export function pavedOne(t: OwnTown, full: OwnTown, id: string, line: number): OwnTown {
  const src = streetsOf(full, id).find((s) => s.line === line);
  if (!src) return t;
  return mapDistrict(t, id, (d) => {
    const had = (d.streets ?? []).some((s) => s.line === line);
    const streets = had ? (d.streets ?? []).map((s) => (s.line === line ? { ...src, paved: true } : s)) : [...(d.streets ?? []), { ...src, paved: true }];
    return { ...d, streets: streets.sort((a, b) => a.line - b.line) };
  });
}

/** The streets paved in `after` that `before` shows unpaved (or not at all), in districts both have. */
export function newlyPaved(before: OwnTown, after: OwnTown): { district: string; line: number }[] {
  const was = new Map(before.districts.map((d) => [d.id, new Map((d.streets ?? []).map((s) => [s.line, s.paved]))]));
  return after.districts.flatMap((d) => {
    const w = was.get(d.id);
    return w ? (d.streets ?? []).filter((s) => s.paved && !w.get(s.line)).map((s) => ({ district: d.id, line: s.line })) : [];
  });
}
